import { useLayoutEffect } from "react"

// Card widths in rem, roomiest first. Every other card metric is derived from
// this one number so the grid always scales as a unit. rem rather than px
// because the app rescales its root font-size by viewport width (see the
// `html { font-size }` media queries in index.css) — in px the cards were the
// one part of the panel that ignored that and stayed put while every label
// around them shrank. The top rung is 2rem, matching the board's own small
// cards: the drawer is the full width of the left sidebar, so there is room for
// thumbnails you can actually recognise rather than colour-coded specks —
// but going bigger than the board made the cards the loudest thing in a panel
// whose point is the deal, not the artwork.
// 0.875rem is the floor — at that size even a player holding every tradable
// property on the board fits in two rows, which is what makes "no scrollbar,
// nothing clipped" actually achievable rather than just aspirational.
const WIDTH_LADDER = [
  2, 1.875, 1.75, 1.625, 1.5, 1.375, 1.25, 1.125, 1, 0.875,
]

// Breathing room, roomiest first, tried in order for each card width. Three
// tiers rather than two because the roomy tier is now genuinely roomy: dropping
// straight from it to the emergency one was a visible lurch, and NORMAL is the
// stop in between that most crowded panels actually land on.
//
// The cash card keeps its own padding knobs (--tc-cash-*) rather than sharing
// the player banners' one: it is the panel's focal point, so it stays roomier
// than they are at every density instead of being squeezed in step with them.
//
// --tc-caption is the one thing a tier is allowed to *remove*: the cash card's
// title row, i.e. the CASH label and the "You keep M120" note beside it. Only on
// a viewport too short for anything else, and even then the row below it still
// spells the deal out in words ("You pay M250 to Sara") and the balance the note
// is derived from is on the player banner one row away. No card, and no control,
// is ever removed.
const ROOMY_DENSITY = {
  "--tc-pad-y": "10px",
  "--tc-stack-gap": "8px",
  "--tc-banner-py": "9px",
  "--tc-grid-pt": "8px",
  "--tc-cash-py": "10px",
  "--tc-cash-gap": "8px",
  "--tc-caption": "flex",
  "--tc-rb": "34px",
}
const NORMAL_DENSITY = {
  "--tc-pad-y": "6px",
  "--tc-stack-gap": "5px",
  "--tc-banner-py": "5px",
  "--tc-grid-pt": "6px",
  "--tc-cash-py": "6px",
  "--tc-cash-gap": "5px",
  "--tc-caption": "flex",
  "--tc-rb": "28px",
}
const DENSE_DENSITY = {
  "--tc-pad-y": "2px",
  "--tc-stack-gap": "2px",
  "--tc-banner-py": "1px",
  "--tc-grid-pt": "3px",
  "--tc-cash-py": "3px",
  "--tc-cash-gap": "3px",
  "--tc-caption": "none",
  "--tc-rb": "24px",
}
const DENSITY_LADDER = [ROOMY_DENSITY, NORMAL_DENSITY, DENSE_DENSITY]

// The card and the panel read all of these (with the roomy values as CSS
// fallbacks), so setting them on an ancestor resizes everything underneath it
// at once.
// Ratios are exact but the values they produce are not (2.5 / 8 = 0.3125), so
// they get trimmed before they reach the DOM.
const rem = (value: number) => `${+value.toFixed(4)}rem`

const apply = (
  el: HTMLElement,
  w: number,
  density: Record<string, string>
) => {
  el.style.setProperty("--tc-w", rem(w))
  el.style.setProperty("--tc-h", rem(w * 1.25))
  el.style.setProperty("--tc-gap", rem(w / 4))
  el.style.setProperty("--tc-gapx", rem(w / 8))
  el.style.setProperty("--tc-strip", rem(w / 4))
  el.style.setProperty("--tc-arrow", rem(w * 0.5625))
  for (const [name, value] of Object.entries(density)) {
    el.style.setProperty(name, value)
  }
}

/**
 * Shrinks the trade panel's property cards until the whole panel fits inside
 * its own height cap.
 *
 * The panel is height-capped so it can never grow past the top of the viewport,
 * but it must not scroll or clip either — so the cards are the thing that gives.
 * Both players' grids read the same custom properties, so one number keeps them
 * visually identical instead of letting one side end up daintier than the other.
 *
 * Deliberately a direct-DOM layout effect rather than component state: it runs
 * before paint (no flash of oversized cards) and re-measuring can never trigger
 * a render, which is what would otherwise turn measure -> resize -> measure into
 * a loop. For the same reason it listens to window resize rather than
 * ResizeObserver'ing the panel — the panel's own height is an *output* of this
 * function, so observing it would be feeding the output back into the input.
 */
export const useFitTradeCards = (
  boxRef: React.RefObject<HTMLElement | null>,
  fitKey: string
) => {
  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return

    const fit = () => {
      // Take the roomiest combination that doesn't overflow, giving up card size
      // before giving up breathing room. Every direct child of the box is
      // `shrink-0`, so an over-stuffed panel genuinely reports
      // scrollHeight > clientHeight instead of quietly squashing its rows.
      for (const density of DENSITY_LADDER) {
        for (const width of WIDTH_LADDER) {
          apply(box, width, density)
          if (box.scrollHeight <= box.clientHeight) return
        }
      }
    }

    fit()
    window.addEventListener("resize", fit)
    return () => window.removeEventListener("resize", fit)
  }, [boxRef, fitKey])
}
